/**
 * Document Verification Page
 *
 * Accessed via /verify/:code
 * Requires login to verify documents
 * Shows verification result with document details
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Button,
} from '@mui/material'
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  ArrowBack,
  Verified,
} from '@mui/icons-material'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'
import { useBranding } from '@context/BrandingContext'
import { formatDateTime } from '@utils/dateUtils'

const DocumentVerification = () => {
  const { code } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, loading: authLoading } = useAuth()
  const { orgShortName, systemSubtitle } = useBranding()

  const [loading, setLoading] = useState(true)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [redirecting, setRedirecting] = useState(false)

  useEffect(() => {
    // Wait for auth to be determined
    if (authLoading) return

    if (!isAuthenticated) {
      // Set redirecting state before navigating
      setRedirecting(true)
      setLoading(false)
      // Redirect to login with return URL
      navigate(`/login?redirect=/verify/${code}`)
      return
    }

    const verifyDocument = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await api.get(`/common/document-registry/verify/?code=${code}`)
        setResult(response.data)
      } catch (err) {
        console.error('Verification error:', err)
        if (err.response?.status === 404) {
          setResult(err.response.data)
        } else if (err.isNetworkError) {
          setError('Network error. Please check your internet connection and try again.')
        } else if (err.response?.status === 401) {
          // Token expired during verification, redirect to login
          setRedirecting(true)
          navigate(`/login?redirect=/verify/${code}`)
          return
        } else {
          setError(err.response?.data?.error || err.userMessage || 'Verification failed. Please try again.')
        }
      } finally {
        setLoading(false)
      }
    }

    verifyDocument()
  }, [code, isAuthenticated, authLoading, navigate])

  const getStatusIcon = () => {
    if (!result?.is_valid) return <ErrorIcon sx={{ fontSize: 80, color: 'error.main' }} />
    if (result.is_revoked) return <Warning sx={{ fontSize: 80, color: 'error.main' }} />
    if (result.is_authentic === false) return <Warning sx={{ fontSize: 80, color: 'warning.main' }} />
    return <CheckCircle sx={{ fontSize: 80, color: 'success.main' }} />
  }

  const getStatusColor = () => {
    if (!result?.is_valid) return 'error'
    if (result.is_revoked) return 'error'
    if (result.is_authentic === false) return 'warning'
    return 'success'
  }

  const getStatusTitle = () => {
    if (!result?.is_valid) return 'Document Not Found'
    if (result.is_revoked) return 'Document Revoked'
    if (result.is_authentic === false) return 'Document Modified'
    if (result.is_authentic === null) return 'Document Found'
    return 'Document Verified'
  }

  if (redirecting) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Redirecting to login...
        </Typography>
      </Box>
    )
  }

  if (authLoading || loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          {authLoading ? 'Checking authentication...' : 'Verifying document...'}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        p: 2,
      }}
    >
      <Paper
        elevation={4}
        sx={{
          maxWidth: 500,
          width: '100%',
          p: 4,
          textAlign: 'center',
          borderRadius: 2,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <img
            src="/images/duonep-logo.png"
            alt="DuoNep"
            style={{ height: 48, marginBottom: 16 }}
          />
          <Typography variant="h5" fontWeight="bold" color="primary.main">
            Document Verification
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {orgShortName ? `${orgShortName} ` : ''}{systemSubtitle}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {error ? (
          <>
            <Alert severity="error" sx={{ my: 2, textAlign: 'left' }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{ mt: 2, mb: 3 }}
            >
              Try Again
            </Button>
          </>
        ) : (
          <>
            {/* Status Icon */}
            <Box sx={{ my: 3 }}>
              {getStatusIcon()}
            </Box>

            {/* Status Title */}
            <Typography
              variant="h5"
              fontWeight="bold"
              color={`${getStatusColor()}.main`}
              gutterBottom
            >
              {getStatusTitle()}
            </Typography>

            {/* Status Message */}
            <Alert
              severity={getStatusColor()}
              sx={{ mb: 3, textAlign: 'left' }}
              icon={result?.is_valid && result?.is_authentic ? <Verified /> : undefined}
            >
              {result?.message}
            </Alert>

            {/* Document Details */}
            {result?.is_valid && (
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                >
                  Document Details
                </Typography>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'grid', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Document Type:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {result.document_type_display}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Document Number:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {result.document_number}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Printed:
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(result.printed_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Printed By:
                      </Typography>
                      <Typography variant="body2">
                        {result.printed_by}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Times Verified:
                      </Typography>
                      <Typography variant="body2">
                        {result.verification_count}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {result.is_revoked && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      This document has been REVOKED
                    </Typography>
                    {result.revocation_reason && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Reason: {result.revocation_reason}
                      </Typography>
                    )}
                  </Alert>
                )}

                {/* Export Metadata for list exports */}
                {result.export_metadata && Object.keys(result.export_metadata).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Export Details:
                    </Typography>
                    <Typography variant="body2">
                      Records: {result.export_metadata.record_count || 'N/A'}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Verification Code Display */}
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Verification Code
              </Typography>
              <Typography
                variant="h6"
                fontFamily="monospace"
                sx={{ letterSpacing: 2 }}
              >
                {code}
              </Typography>
            </Box>
          </>
        )}

        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          variant="outlined"
          fullWidth
        >
          Go to Dashboard
        </Button>
      </Paper>
    </Box>
  )
}

export default DocumentVerification
